import React, { useState } from 'react';
import { CheckCircle, Shield, Clock } from 'lucide-react';
import { MultiStepForm } from '../components/Trial/MultiStepForm';
import { SEOHead } from '../components/SEO/SEOHead';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/AcquireFooter';
export function FreeTrialPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const handleMultiStepSuccess = () => {
    setShowSuccessModal(true);
  };
  return <>
      <SEOHead title="Get Started | AcquireFlow" description="Get started with AcquireFlow for just $97. Includes a 1-on-1 onboarding call and is credited toward your first month. Send automated MLS offers and close more deals." canonical="https://acquireflow.com/trial" data-id="element-2615" />

      <div className="min-h-screen bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212]" data-id="element-2616">
        <Navbar data-id="element-2617" />

        <div className="relative pt-20 pb-20" data-id="element-2618">
          {/* Floating Particles Background - matching hero section exactly */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-2619">
            {[...Array(20)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-[#3ab795] rounded-full opacity-20" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float-particle ${15 + Math.random() * 15}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`
          }} data-id="element-2620" />)}
          </div>

          <div className="max-w-[1400px] mx-auto px-6 md:px-8" data-id="element-2621">
            {/* Compact Hero Section */}
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 relative z-10" data-id="element-2622">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4 backdrop-blur-sm animate-fade-in" data-id="element-2623">
                <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-2624" />
                <span className="text-sm font-medium text-[#3ab795] uppercase tracking-wide" data-id="element-2625">
                  $97 Onboarding • Credited to First Month
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-[1.1] tracking-tight animate-fade-in" style={{
              animationDelay: '100ms'
            }} data-id="element-2626">
                Send Your First 1,000 Offers{' '}
                <span className="text-[#3ab795] relative inline-block" data-id="element-2627">
                  This Week
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[#3ab795]/30 rounded-full" data-id="element-2628"></span>
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-base md:text-lg lg:text-xl text-[#b8bcc8] leading-relaxed mb-8 animate-fade-in" style={{
              animationDelay: '200ms'
            }} data-id="element-2629">
                Join 500+ real estate investors using AcquireFlow to automate
                MLS offers and triple their deal volume.
              </p>

              {/* Trust Badges - Compact */}
              <div className="flex flex-wrap justify-center gap-2 md:gap-3 animate-fade-in" style={{
              animationDelay: '300ms'
            }} data-id="element-2630">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white" data-id="element-2631">
                  <Clock className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2632" />
                  <span data-id="element-2633">1-on-1 setup call</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-lg text-sm font-medium text-[#3ab795]" data-id="element-2634">
                  <Shield className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2635" />
                  <span data-id="element-2636">30-day money-back</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white" data-id="element-2637">
                  <CheckCircle className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2638" />
                  <span data-id="element-2639">$97 credited to first month</span>
                </div>
              </div>
            </div>

            {/* MAIN FOCUS: Centered Form - Hero Treatment */}
            <div className="max-w-2xl mx-auto relative z-10" data-id="element-2640">
              <div className="relative animate-fade-in" style={{
              animationDelay: '400ms'
            }} data-id="element-2641">
                {/* White glow effect - matching hero section video */}
                <div className="absolute inset-0 rounded-3xl blur-3xl bg-white opacity-30" style={{
                transform: 'scale(0.92)'
              }} data-id="element-2642" />

                <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-white/10 rounded-2xl shadow-[0_20px_60px_rgba(255,255,255,0.1)] p-8 sm:p-12 transition-all duration-300 hover:border-white/20 hover:shadow-[0_24px_80px_rgba(255,255,255,0.15)]" data-id="element-2643">
                  {/* Form Header */}
                  <div className="text-center mb-8 pb-6 border-b border-white/10" data-id="element-2644">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4" data-id="element-2645">
                      <Clock className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2646" />
                      <span className="text-sm font-medium text-[#3ab795]" data-id="element-2647">
                        Takes 60 seconds
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-2648">
                      Get Started with AcquireFlow
                    </h2>
                    <p className="text-base md:text-lg text-[#b8bcc8]" data-id="element-2649">
                      $97 onboarding fee credited toward your first month.
                    </p>
                  </div>

                  <MultiStepForm onSuccess={handleMultiStepSuccess} data-id="element-2650" />
                </div>
              </div>

              {/* Terms */}
              <p className="mt-6 text-center text-sm text-[#b8bcc8]/70" data-id="element-2651">
                By signing up, you agree to our{' '}
                <a href="/terms-and-conditions" className="text-[#3ab795] hover:text-[#2d9f7e] underline transition-colors" data-id="element-2652">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy-policy" className="text-[#3ab795] hover:text-[#2d9f7e] underline transition-colors" data-id="element-2653">
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>

        <Footer data-id="element-2654" />
      </div>

      {/* Success Modal */}
      {showSuccessModal && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" data-id="element-2655">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-white/10 rounded-2xl p-8 md:p-10 max-w-md w-full text-center shadow-2xl" data-id="element-2656">
            <div className="w-20 h-20 bg-[#3ab795]/10 rounded-full flex items-center justify-center mx-auto mb-6 relative" data-id="element-2657">
              <div className="absolute inset-0 bg-[#3ab795] rounded-full animate-ping opacity-20" data-id="element-2658"></div>
              <CheckCircle className="w-10 h-10 text-[#3ab795] relative z-10" strokeWidth={2} data-id="element-2659" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-2660">
              Welcome to AcquireFlow!
            </h3>
            <p className="text-[#b8bcc8] mb-8 text-lg" data-id="element-2661">
              You'll be redirected to book your onboarding call. Check your
              email for confirmation details.
            </p>
            <button onClick={() => setShowSuccessModal(false)} className="w-full px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-xl hover:bg-[#2d9f7e] transition-all duration-300 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#0a0a0a]" data-id="element-2662">
              Got it
            </button>
          </div>
        </div>}
    </>;
}